import { z } from "zod";
import { optionalUnlessRequired, requiredText, type FieldDescriptor } from "./types.js";

// Composite: street, city, LGA, state, country, postcode (docs/01-PRODUCT-SPEC.md §3.2).
const configSchema = z.object({
  showPostcode: z.boolean().default(false),
  showCountry: z.boolean().default(false),
  defaultCountry: z.string().default("Nigeria"),
});

type AddressConfig = z.infer<typeof configSchema>;

export const addressField: FieldDescriptor<AddressConfig> = {
  type: "address",
  label: "Address",
  category: "basic_info",
  icon: "MapPin",
  configSchema,
  valueSchema: (config, isRequired) => {
    const shape: Record<string, z.ZodTypeAny> = {
      street: isRequired ? requiredText() : z.string().trim().optional(),
      city: isRequired ? requiredText() : z.string().trim().optional(),
      lga: z.string().trim().optional(),
      state: isRequired ? requiredText() : z.string().trim().optional(),
    };
    if (config.showCountry) shape.country = z.string().trim().optional();
    if (config.showPostcode) shape.postcode = z.string().trim().optional();

    return optionalUnlessRequired(z.object(shape).strict(), isRequired);
  },
  defaultConfig: { showPostcode: false, showCountry: false, defaultCountry: "Nigeria" },
  supportsProfileBinding: true,
  profileParts: {
    street: "address.street",
    city: "address.city",
    lga: "address.lga",
    state: "address.state",
    country: "address.country",
    postcode: "address.postal_code",
  },
  defaultSearchable: true,
  renderHints: { isComposite: true },
};
